import { rgba } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { theme, ifProp, withProp } from 'styled-tools';

import Icon from '@ui/icons/Icon.tsx';
import Text from '@ui/text/Text.tsx';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import SearchResultsIndicator from './SearchResultsIndicator.tsx';

const BuildingRowButton = styled('button')`
  all: unset;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${theme('colors.primaryButtonText')};
  border-bottom: 1px solid ${theme('colors.mapNavBackground')};
  background-color: ${withProp(theme('colors.mapNavBackground'), mapNavBackground => rgba(mapNavBackground, 0.85))};

  &:hover {
    background-color: ${theme('colors.mapNavBackground')};
  }

  &:focus-visible {
    outline: var(--focus-outline);
    outline-offset: -3px;
  }

  ${ifProp(
    '$isSelected',
    css`
      background-color: ${theme('colors.mapNavBackground')};
    `,
    '',
  )}
`;

const BuildingRowText = styled(Text)`
  flex: 1 1 auto;
  word-break: break-word;
  margin-top: 5px;
  margin-bottom: 5px;
`;

const StyledSearchResultsIndicator = styled(SearchResultsIndicator)`
  margin-inline-start: 2px;
`;

// treeTabIndex / treeOnKeyDown / isTreeFocused support ARIA tree roving tabindex.
const BuildingRow = ({ ref, ...props }) => {
  const colors = useCSSColors();
  const {
    id,
    name,
    resultsNumber,
    selectedBuildingId,
    onBuildingClick,
    treeTabIndex,
    treeOnKeyDown,
    isTreeFocused,
    ariaExpanded,
    ariaControls,
    T,
  } = props;

  const createAriaLabel = resultsNumber => {
    return resultsNumber
      ? `${name}, ${T('mapLevelSelectorOnline:_resultsNumber_ search result within this building', { count: resultsNumber, resultsNumber })}`
      : `${name}`; // If there are no results just the name
  };

  return (
    <BuildingRowButton
      data-testid="BuildingRow"
      ref={ref}
      onClick={() => onBuildingClick(id)}
      $isSelected={selectedBuildingId && selectedBuildingId === id}
      role="treeitem"
      aria-label={createAriaLabel(resultsNumber)}
      aria-selected={id === selectedBuildingId}
      type="button"
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      tabIndex={typeof treeTabIndex === 'number' ? treeTabIndex : 0}
      onKeyDown={
        treeOnKeyDown ||
        (e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onBuildingClick(id);
          }
        })
      }
      data-tree-focused={isTreeFocused ? 'true' : undefined}
    >
      <Icon
        name="triangle.down"
        iconSize="md"
        fillColor={colors.mapNavText}
        height="32px"
        width="32px"
        degreesOfIconRotation={selectedBuildingId && selectedBuildingId === id ? 0 : -90}
        aria-hidden="true"
      />
      <BuildingRowText fontSize="md" fontWeight="500" limitLinesOfTextTo={1} aria-hidden="true">
        {name}
      </BuildingRowText>
      {resultsNumber && <StyledSearchResultsIndicator resultsNumber={resultsNumber} />}
    </BuildingRowButton>
  );
};

BuildingRow.displayName = 'Building Row';

export default BuildingRow;
